import api from './api';
import type { ApiResponse } from '@/types';

export interface MockConversation {
  role: 'user' | 'assistant';
  content: string;
  delay_ms?: number;
}

export interface MockResponse {
  response: string;
  conversationId?: string;
}

export interface SimulateConversationParams {
  persona: string;
  productDescription: string;
  numTurns?: number;
  includeDelays?: boolean;
}

export interface SimulateChatbotParams {
  projectId: string;
  message: string;
  botPersona: string;
  conversationContext?: Array<{ role: string; content: string }>;
}

export interface MockDataParams {
  schema: Record<string, unknown> | string;
  count?: number;
  includeEdgeCases?: boolean;
}

export interface ProjectDashboard {
  projects: Array<{
    id: string;
    name: string;
    role: string;
    progress: number;
    incompleteTasks: number;
    status: string;
  }>;
  todayTasks: Array<{
    id: string;
    projectId: string;
    projectName: string;
    title: string;
    dueDate: string;
    priority: string;
  }>;
  summary: {
    totalProjects: number;
    activeProjects: number;
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
  };
}

export const mockingService = {
  /**
   * Simulate a chatbot response for prototype testing
   */
  async simulateChatbot(params: SimulateChatbotParams): Promise<MockResponse> {
    const response = await api.post<ApiResponse<MockResponse>>('/mocking/chatbot', params);
    return response.data.data;
  },

  /**
   * Simulate a multi-turn conversation
   */
  async simulateConversation(params: SimulateConversationParams): Promise<MockConversation[]> {
    const response = await api.post<ApiResponse<MockConversation[]>>(
      '/mocking/conversation',
      params
    );
    return response.data.data;
  },

  /**
   * Generate mock data based on schema
   */
  async generateMockData(params: MockDataParams): Promise<Array<Record<string, unknown>>> {
    const response = await api.post<ApiResponse<Array<Record<string, unknown>>>>(
      '/mocking/data',
      params
    );
    return response.data.data;
  },

  /**
   * Get project dashboard data for global chat
   */
  async getProjectDashboard(): Promise<ProjectDashboard> {
    const response = await api.get<ApiResponse<ProjectDashboard>>('/dashboard/overview');
    return response.data.data;
  },

  /**
   * Get today's tasks across all projects
   */
  async getTodayTasks(): Promise<ProjectDashboard['todayTasks']> {
    const response = await api.get<ApiResponse<ProjectDashboard['todayTasks']>>(
      '/dashboard/today-tasks'
    );
    return response.data.data;
  },

  /**
   * Get task summary for a specific project
   */
  async getProjectTaskSummary(projectId: string): Promise<{
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
    tasksByStatus: Record<string, number>;
  }> {
    const response = await api.get<
      ApiResponse<{
        totalTasks: number;
        completedTasks: number;
        pendingTasks: number;
        tasksByStatus: Record<string, number>;
      }>
    >(`/projects/${projectId}/task-summary`);
    return response.data.data;
  },
};
